import { useEffect, useRef, useState } from "react";
import type { Backdrop } from "../assets";
import { PauseIcon, PlayIcon } from "../icons";

/**
 * A full-bleed hero background: a still, and the loop that brings it to life.
 *
 * 🔴 THE STILL IS THE PAGE; THE VIDEO IS A BONUS, AND IT HAS TO EARN ITS BYTES.
 * The loop is 1–2.5 MB on a product sold onto Indian school connections, so it
 * plays only when every one of these holds:
 *
 *   · the screen is at least tablet-wide — a phone gets the still, which is the
 *     same picture, and keeps its data for the page itself;
 *   · motion is allowed — the OS setting or the in-app toggle, read from the
 *     `data-reduce-motion` stamp `useApplyReduceMotion` puts on `<html>`;
 *   · the reader has not asked to save data (`navigator.connection.saveData`)
 *     and is not on a 2G/3G link;
 *   · the hero is on screen — it pauses when scrolled away and resumes on return.
 *
 * ⚠️ `preload="none"` AND NO `autoPlay` ATTRIBUTE. The element is only rendered
 * once the checks above pass, and `play()` is only called while it is visible,
 * so a reader who never sees the hero never downloads a byte of video.
 *
 * ⚠️ THE CONTROL IS NOT OPTIONAL. WCAG 2.2.2: anything that moves for more than
 * five seconds alongside other content needs a way to pause it. It is rendered
 * only while there is a video to pause — a "pause" button on a still picture
 * would be a control that does nothing.
 */
export interface BackdropVideoProps {
  backdrop: Backdrop;
  /** The control's labels. */
  pauseLabel: string;
  playLabel: string;
  /** Where the control sits, in the hero's own coordinates. */
  controlClassName?: string;
  /** Focus point for `object-position`, so the subject survives a narrow crop. */
  objectPosition?: string;
}

export function BackdropVideo({
  backdrop,
  pauseLabel,
  playLabel,
  controlClassName = "",
  objectPosition = "center",
}: BackdropVideoProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const allowed = useMotionBudget();
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [ready, setReady] = useState(false);

  // Is the hero on screen? Drives play/pause so an off-screen loop costs nothing.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry?.isIntersecting ?? false);
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (allowed && visible && !paused) {
      // Autoplay can still be refused (Low Power Mode, a strict browser policy).
      // The still is already on screen, so a refusal is simply no motion.
      video.play().catch(() => {
        setReady(false);
      });
    } else {
      video.pause();
    }
  }, [allowed, visible, paused]);

  const style = { objectPosition };

  return (
    <div ref={rootRef} className="absolute inset-0 overflow-hidden" aria-hidden={!allowed}>
      <picture>
        <source media="(max-width: 767px)" srcSet={backdrop.stillSmall} />
        <img
          src={backdrop.still}
          alt=""
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover"
          style={style}
        />
      </picture>

      {allowed ? (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          poster={backdrop.still}
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={() => {
            setReady(true);
          }}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-dash ${
            ready ? "opacity-100" : "opacity-0"
          }`}
          style={style}
        >
          <source src={backdrop.webm} type="video/webm" />
          <source src={backdrop.mp4} type="video/mp4" />
        </video>
      ) : null}

      {allowed ? (
        <button
          type="button"
          onClick={() => {
            setPaused((p) => !p);
          }}
          aria-pressed={paused}
          aria-label={paused ? playLabel : pauseLabel}
          className={`pointer-events-auto z-20 inline-flex h-10 items-center gap-2.5 rounded-pill border border-surface/60 bg-surface/20 pr-4 pl-1.5 font-dash text-dash-xs font-extrabold text-surface backdrop-blur-md outline-none transition-colors duration-150 ease-dash hover:bg-surface/30 focus-visible:ring-2 focus-visible:ring-surface ${controlClassName}`}
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-pill bg-surface text-ink">
            {paused ? <PlayIcon className="h-3.5 w-3.5" /> : <PauseIcon className="h-3.5 w-3.5" />}
          </span>
          <span className="hidden sm:inline">{paused ? playLabel : pauseLabel}</span>
        </button>
      ) : null}
    </div>
  );
}

/**
 * May a background loop play at all, given the screen, the reader's motion
 * preference and their connection?
 *
 * ⚠️ IT WATCHES THE `data-reduce-motion` STAMP RATHER THAN THE MEDIA QUERY.
 * `useApplyReduceMotion` folds the OS preference and the in-app toggle into
 * that one attribute; reading the media query here would ignore a teacher who
 * switched motion off in Settings.
 */
function useMotionBudget(): boolean {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof matchMedia !== "function") return;
    const wide = matchMedia("(min-width: 768px)");
    const conn = (
      navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string };
      }
    ).connection;

    const resolve = (): void => {
      const reduce = document.documentElement.dataset.reduceMotion === "true";
      const slow = conn?.saveData === true || /(^|-)2g$|^3g$/.test(conn?.effectiveType ?? "");
      setAllowed(wide.matches && !reduce && !slow);
    };

    resolve();
    wide.addEventListener("change", resolve);
    const stamp = new MutationObserver(resolve);
    stamp.observe(document.documentElement, { attributes: true, attributeFilter: ["data-reduce-motion"] });
    return () => {
      wide.removeEventListener("change", resolve);
      stamp.disconnect();
    };
  }, []);

  return allowed;
}
